import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Badge } from '../../../components/ui/badge';
import { productLabel } from './productLabels';
import { Button } from '../../../components/ui/button';
import {
  Loader2, AlertTriangle, CreditCard, Package, History, ShieldCheck, Info,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * The tenant's commercial state, in one place.
 *
 * Subscription and Billing were separate tabs reading different endpoints —
 * one the raw tenant document, one a computed billing summary — and they
 * disagreed about the same tenant. This reads ONE composed endpoint.
 *
 * TWO THINGS THIS SCREEN REFUSES TO DO:
 *
 *   1. Recompute the agreement. An active subscription shows its CAPTURED
 *      totals. Re-pricing it against today's catalogue would show a number the
 *      customer never agreed to.
 *   2. Conflate sold with granted. "Subscription" is what was agreed;
 *      "Entitlements" is what the tenant may actually use. They are rendered
 *      as separate panels, and the gap between them is shown rather than
 *      smoothed over.
 */

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-700',
  draft: 'bg-slate-100 text-slate-600',
  suspended: 'bg-amber-100 text-amber-700',
  ended: 'bg-red-100 text-red-700',
};

const Money = ({ amount, currency }) =>
  amount === null || amount === undefined ? (
    // Never a zero. The server could not price this.
    <span className="text-amber-700 font-medium">Not priced</span>
  ) : (
    <span>{currency} {Number(amount).toFixed(2)}</span>
  );

export default function SubscriptionBillingTab({ tenantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/api/admin/tenants/${tenantId}/commercial-summary`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      setData(d);
      setError(null);
    } catch (e) {
      // A failed fetch is not an empty tenant. Saying nothing here would
      // render "no subscription" over a tenant that may well have one.
      setData(null);
      setError(e?.response?.status === 404
        ? 'Tenant not found.'
        : `Commercial state could not be loaded (${e?.response?.status || 'network error'}).`);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-10">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading commercial state…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="commercial-error">
        <p className="text-sm font-medium text-amber-800">Commercial state unavailable</p>
        <p className="text-sm text-amber-700 mt-1">{error}</p>
        <p className="text-xs text-amber-600 mt-2">
          This is not a report that the tenant has no subscription — nothing was read.
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={load}>Retry</Button>
      </div>
    );
  }

  const sub = data?.subscription;
  const totals = data?.totals;
  const drift = data?.drift || {};
  const billing = data?.billing || {};
  // Code -> product name, from the server's registry (`product_labels()`).
  // Nothing on this tab prints an item_code or app_code directly.
  const labels = data?.labels || {};
  const named = (codes) => (codes || []).map((c) => productLabel(c, labels)).join(', ');
  // Products sold once for the workspace, from the catalogue. Their agreement
  // line holds quantity 1 — one workspace, which prices the per-tenant figure
  // once — and a bare "1" in a Qty column reads as one user.
  const tenantLevel = new Set(data?.tenant_level_products || []);
  const hasDrift = (drift.granted_not_sold?.length || 0) + (drift.sold_not_granted?.length || 0) > 0;
  const capabilityRows = Object.entries(data?.capabilities || {})
    .flatMap(([product, codes]) => (codes || []).map((code) => ({ product, code })));

  return (
    <div className="space-y-5" data-testid="subscription-billing-tab">
      <section className="rounded-lg border border-slate-200 bg-slate-50 p-4"
               data-testid="commercial-model-guide">
        <h3 className="text-sm font-semibold mb-2">How this tenant's commercial state fits together</h3>
        <div className="grid gap-2 md:grid-cols-5 text-xs">
          <div><strong className="block">Agreement</strong><span className="text-slate-500">Products and add-ons sold</span></div>
          <div><strong className="block">Product access</strong><span className="text-slate-500">Runtime modules enabled</span></div>
          <div><strong className="block">Add-on access</strong><span className="text-slate-500">Capabilities enabled within a product</span></div>
          <div><strong className="block">Licences</strong><span className="text-slate-500">Seat pools; managed in Licenses</span></div>
          <div><strong className="block">Capacity</strong><span className="text-slate-500">Included, purchased and granted limits; managed in Limits</span></div>
        </div>
      </section>
      {/* ---------------- the agreement ---------------- */}
      <section className="rounded-lg border border-slate-200 p-5">
        <header className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Package className="h-4 w-4" /> Current subscription
          </h3>
          {sub ? (
            <Badge className={STATUS_STYLES[sub.status] || 'bg-slate-100 text-slate-600'}>
              {String(sub.status || '').toUpperCase()}
            </Badge>
          ) : null}
        </header>

        {!sub ? (
          <p className="text-sm text-slate-500" data-testid="no-subscription">
            No subscription has been created for this tenant yet.
          </p>
        ) : (
          <>
            {sub.status !== 'active' && (
              <p className="text-xs text-slate-500 mb-3 flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5" />
                This agreement is {sub.status} — the figures below are a live quote, not a
                commitment.
              </p>
            )}

            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div><dt className="text-slate-500 text-xs">Price book</dt>
                   <dd>{sub.price_book_code || 'Default'}</dd></div>
              <div><dt className="text-slate-500 text-xs">Bundle</dt>
                   <dd>{sub.bundle_code || '—'}</dd></div>
              <div><dt className="text-slate-500 text-xs">Term</dt>
                   <dd>{sub.term || '—'}</dd></div>
              <div><dt className="text-slate-500 text-xs">Currency</dt>
                   <dd>{sub.currency || '—'}</dd></div>
              <div><dt className="text-slate-500 text-xs">Starts</dt>
                   <dd>{sub.starts_at ? new Date(sub.starts_at).toLocaleDateString() : '—'}</dd></div>
              <div><dt className="text-slate-500 text-xs">Ends</dt>
                   <dd>{sub.ends_at ? new Date(sub.ends_at).toLocaleDateString() : '—'}</dd></div>
              <div><dt className="text-slate-500 text-xs">Renews</dt>
                   <dd>{sub.renews_at ? new Date(sub.renews_at).toLocaleDateString() : '—'}</dd></div>
              <div><dt className="text-slate-500 text-xs">Auto-renew</dt>
                   <dd>{sub.auto_renew ? 'Yes' : 'No'}</dd></div>
            </dl>

            {/* lines, with the prices that were CAPTURED */}
            <table className="w-full text-sm" data-testid="subscription-lines">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2">Product</th><th>Capabilities</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Unit</th><th className="text-right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {(totals?.lines || []).map((l, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 font-medium">
                      {productLabel(l.item_code, labels)}
                      {l.negotiated && (
                        <Badge variant="outline" className="ml-2 text-[10px]">negotiated</Badge>
                      )}
                    </td>
                    <td className="text-slate-500">
                      {named(l.capabilities) || '—'}
                    </td>
                    <td className="text-right" data-testid={`subscription-qty-${l.item_code}`}>
                      {tenantLevel.has(l.item_code) && l.item_kind !== 'bundle'
                        ? 'Whole workspace' : l.quantity}
                    </td>
                    <td className="text-right"><Money amount={l.unit_price} currency={totals?.currency} /></td>
                    <td className="text-right"><Money amount={l.line_total} currency={totals?.currency} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 flex items-center justify-end gap-6 text-sm">
              {totals?.discount_total > 0 && (
                <span className="text-slate-500">
                  Discount −<Money amount={totals.discount_total} currency={totals.currency} />
                </span>
              )}
              <span className="font-semibold text-base" data-testid="subscription-total">
                {totals?.priced
                  ? <Money amount={totals.total} currency={totals.currency} />
                  : <span className="text-amber-700">Not priced</span>}
              </span>
            </div>
            {sub.status === 'active' && (
              <p className="text-xs text-slate-400 mt-1 text-right">
                Captured at activation — not recalculated from today's price book
              </p>
            )}
          </>
        )}
      </section>

      {/* ---------------- what they may actually use ---------------- */}
      <section className="rounded-lg border border-slate-200 p-5">
        <h3 className="text-base font-semibold flex items-center gap-2 mb-1">
          <ShieldCheck className="h-4 w-4" /> Entitlements
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          What this tenant may operationally use, read from its workspace. Separate
          from the agreement above — these move for different reasons.
        </p>
        {/* RECORDED HERE, REFUSED BY THE WORKSPACE. The server reads the
            runtime's own rows; a product Admin wrote that never landed is
            named instead of being listed as enabled. */}
        {(data.not_landed || []).length > 0 && (
          <p className="mb-3 text-sm text-amber-700" data-testid="entitlements-not-landed">
            Recorded but not live in the workspace: <strong>{named(data.not_landed)}</strong> —
            switch them on from the Modules tab.
          </p>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="entitlements">
          {(data.entitlements || []).filter((e) => e.enabled).length === 0 && (
            <span className="text-sm text-slate-500">No modules currently enabled.</span>
          )}
          {(data.entitlements || []).filter((e) => e.enabled).map((e) => (
            <Badge key={e.app_code} variant="outline" className="text-xs">
              {productLabel(e.app_code, labels)}
            </Badge>
          ))}
        </div>

        <div className="mt-4 border-t border-slate-100 pt-3" data-testid="add-on-capabilities">
          <h4 className="text-sm font-medium">Add-on capabilities</h4>
          <p className="text-xs text-slate-500 mt-0.5 mb-2">
            Feature access within a product. These are not separate runtime modules or seat pools.
          </p>
          {capabilityRows.length === 0 ? (
            <span className="text-sm text-slate-500">No add-on capabilities enabled.</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {capabilityRows.map(({ product, code }) => (
                <Badge key={`${product}:${code}`} variant="outline" className="text-xs">
                  {productLabel(code, labels)} · within {productLabel(product, labels)}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {hasDrift && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="commercial-drift">
            <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" /> Agreement and entitlements differ
            </p>
            {drift.sold_not_granted?.length > 0 && (
              <p className="text-sm text-amber-700 mt-1">
                Sold but not enabled: <strong>{named(drift.sold_not_granted)}</strong> —
                the customer is paying for this and cannot use it.
              </p>
            )}
            {drift.granted_not_sold?.length > 0 && (
              <p className="text-sm text-amber-700 mt-1">
                Enabled but not on the agreement: <strong>{named(drift.granted_not_sold)}</strong>.
              </p>
            )}
          </div>
        )}
      </section>

      {/* ---------------- billing ---------------- */}
      <section className="rounded-lg border border-slate-200 p-5">
        <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
          <CreditCard className="h-4 w-4" /> Billing
        </h3>
        {!billing.provider_configured ? (
          <p className="text-sm text-slate-500" data-testid="billing-not-configured">
            No billing provider is connected for this tenant. Nothing is being charged
            through the platform.
          </p>
        ) : (
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><dt className="text-slate-500 text-xs">Status</dt>
                 <dd>{billing.billing_status || 'Not recorded'}</dd></div>
            <div><dt className="text-slate-500 text-xs">Billing email</dt>
                 <dd>{billing.billing_email || '—'}</dd></div>
            <div><dt className="text-slate-500 text-xs">Next billing</dt>
                 <dd>{billing.next_billing_date
                        ? new Date(billing.next_billing_date).toLocaleDateString() : '—'}</dd></div>
            <div><dt className="text-slate-500 text-xs">Customer</dt>
                 <dd className="font-mono text-xs">{billing.stripe_customer_id || '—'}</dd></div>
          </dl>
        )}
        {billing.is_trial && (
          <p className="text-xs text-slate-500 mt-2">
            Trial{billing.trial_ends_at
              ? ` ends ${new Date(billing.trial_ends_at).toLocaleDateString()}` : ''}
          </p>
        )}
      </section>

      {/* ---------------- history ---------------- */}
      <section className="rounded-lg border border-slate-200 p-5">
        <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
          <History className="h-4 w-4" /> Subscription history
        </h3>
        {(data.history || []).length === 0 ? (
          <p className="text-sm text-slate-500">No lifecycle events recorded.</p>
        ) : (
          <ul className="space-y-2 text-sm" data-testid="subscription-history">
            {[...data.history].reverse().map((h, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-xs text-slate-400 w-40 shrink-0">
                  {h.at ? new Date(h.at).toLocaleString() : '—'}
                </span>
                <span>
                  <strong>{h.from} → {h.to}</strong>
                  {h.by ? <span className="text-slate-500"> by {h.by}</span> : null}
                  {h.reason ? <span className="text-slate-500"> · {h.reason}</span> : null}
                  {h.total !== undefined && h.total !== null
                    ? <span className="text-slate-500"> · {h.currency} {Number(h.total).toFixed(2)}</span>
                    : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
